import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import type {
    AdvanceServiceRequestStatusArgs,
    AssignServiceRequestArgs,
    CancelServiceRequestArgs,
    FieldAdvanceStageArgs,
    FieldClearWeatherHoldArgs,
    FieldSetWeatherHoldArgs,
    Job,
    ScheduleStageArgs,
    ServiceRequest,
    SubmitServiceRequestArgs,
    UpsertJobArgs,
    WeatherHold,
} from './database.types';
import { queryKeys } from './queries';

// -----------------------------------------------------------------------------
// field_advance_stage - field and admin can advance stage status
// -----------------------------------------------------------------------------
// Not idempotent in itself but safe to retry because status transitions are
// additive (you can call "in_progress" twice without breaking anything).
export function useAdvanceStage() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: FieldAdvanceStageArgs) => {
            const { data, error } = await supabase.rpc('field_advance_stage', args);
            if (error) throw error;
            return data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
            qc.invalidateQueries({ queryKey: ['activityLog'] });
        },
    });
}

// -----------------------------------------------------------------------------
// schedule_stage - admin only. Sets scheduled_date + crew_id atomically.
// -----------------------------------------------------------------------------
export function useScheduleStage() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: ScheduleStageArgs) => {
            const { data, error } = await supabase.rpc('schedule_stage', args);
            if (error) throw error;
            return data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
            qc.invalidateQueries({ queryKey: ['activityLog'] });
        },
    });
}

/**
 * Stages the waterfall accepts as a starting point. Mirrors the RPC.
 *
 * Pour is absent on purpose: it is the last stage, so anchoring on it would
 * write nothing.
 */
export type CascadeAnchor =
    | 'Pin'
    | 'Box'
    | 'Plumbing Inspection'
    | 'Prep'
    | 'Slab Inspection';

export interface WaterfallScheduleArgs {
    p_job_id: string;
    p_from_stage: CascadeAnchor;
    p_date: string;
}

// -----------------------------------------------------------------------------
// waterfall_schedule - admin only. Fills downstream stage dates from an anchor.
//
// Three changes from the original Pin-only version, all from Andrea:
//   1. Offsets are BUSINESS days. The old calendar offsets counted weekends, so
//      derived dates kept landing on Saturdays.
//   2. Box and Prep cascade too, not just Pin. Those are the dates she actually
//      re-enters when a job moves.
//   3. As of 28 Jul the spacing is one business day per stage, so a Monday Pin
//      gives Box Tuesday through Pour the following Monday. The previous
//      offsets were derived from ~585 real rows and described how the work
//      genuinely paces, but she asked for even spacing four times, the last
//      time with a screenshot from the fresh-job test we asked her to run.
//      These are prefilled starting dates on a board she edits freely.
//   4. As of 31 Jul every stage except Pour can anchor, not just Pin, Box and
//      Prep. Uniform offsets made the old three-anchor restriction arbitrary,
//      and her actual case is a mid-job slip: "if I have to go in and change
//      the plumbing date because it got pushed, any task after that should
//      automatically waterfall".
//
// Stages above the anchor are never touched, and anything already in_progress
// or complete is left alone, so a projection can never overwrite work that
// really happened.
// -----------------------------------------------------------------------------
export function useWaterfallSchedule() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: WaterfallScheduleArgs) => {
            const { data, error } = await supabase.rpc('waterfall_schedule', args);
            if (error) throw error;
            return data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
            qc.invalidateQueries({ queryKey: ['activityLog'] });
        },
    });
}

// -----------------------------------------------------------------------------
// field_set_weather_hold - idempotent server-side
// -----------------------------------------------------------------------------
export function useSetWeatherHold() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: FieldSetWeatherHoldArgs): Promise<WeatherHold> => {
            const { data, error } = await supabase.rpc('field_set_weather_hold', args);
            if (error) throw error;
            return data as WeatherHold;
        },
        onSuccess: (_data, variables) => {
            qc.invalidateQueries({ queryKey: queryKeys.weatherHolds.active(variables.p_job_id) });
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
            qc.invalidateQueries({ queryKey: ['activityLog'] });
        },
    });
}

// -----------------------------------------------------------------------------
// field_clear_weather_hold - idempotent server-side
// -----------------------------------------------------------------------------
export function useClearWeatherHold() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: FieldClearWeatherHoldArgs): Promise<WeatherHold> => {
            const { data, error } = await supabase.rpc('field_clear_weather_hold', args);
            if (error) throw error;
            return data as WeatherHold;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['weatherHolds'] });
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
            qc.invalidateQueries({ queryKey: ['activityLog'] });
        },
    });
}

// -----------------------------------------------------------------------------
// upsert_job - admin only. Creates or updates by composite key.
// -----------------------------------------------------------------------------
export function useUpsertJob() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: UpsertJobArgs): Promise<Job> => {
            const { data, error } = await supabase.rpc('upsert_job', args);
            if (error) throw error;
            return data as Job;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
            qc.invalidateQueries({ queryKey: queryKeys.communities });
        },
    });
}

// -----------------------------------------------------------------------------
// Direct job edits (admin only, via table UPDATE under RLS)
// -----------------------------------------------------------------------------

/**
 * Fields the board is allowed to patch directly.
 *
 * lot_number is editable now because Andrea appends and removes markers like
 * "New Start" and "HOLD" as a job moves, and locking the field meant she could
 * never take them back off.
 *
 * Worth knowing what that costs: upsert_job matches on
 * (builder_id, community_id, lot_number) exactly, so renaming a lot breaks the
 * match and the next PO for it inserts a second row instead of updating this
 * one. That is precisely how the Westfall 210-215 pairs appeared. The real fix
 * is normalised matching in the PO sync; until then the edit dialog warns
 * before saving a changed lot number.
 *
 * crew_id here is the job level crew, not the per-stage one. Both exist and
 * neither replaces the other.
 */
export type JobPatch = Partial<
    Pick<
        Job,
        | 'address'
        | 'contact_name'
        | 'contact_phone'
        | 'notes'
        | 'needs_review'
        | 'review_reason'
        | 'flatwork_active'
        | 'lot_number'
        | 'fw_ready_date'
        | 'fw_ready_confirmed'
        | 'force_active'
        | 'crew_id'
    >
>;

export function useUpdateJob() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, patch }: { id: string; patch: JobPatch }) => {
            const { data, error } = await supabase
                .from('jobs')
                .update(patch)
                .eq('id', id)
                .select()
                .single();
            if (error) throw error;
            return data as Job;
        },
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
            qc.invalidateQueries({ queryKey: queryKeys.jobs.detail(data.id) });
        },
    });
}

// -----------------------------------------------------------------------------
// Board placement: pull a completed job back, or send it forward again
// -----------------------------------------------------------------------------

/**
 * force_active pins a job to the active board regardless of what its stages
 * say, which is how a job marked complete by mistake comes back.
 *
 * It deliberately does not touch stage history. Reopening by clearing Pour's
 * actual_completion_date would silently break that lot's weather packet, since
 * the prep-to-pour window is read from exactly that date, and a packet already
 * sent to a builder cannot be regenerated identically.
 */
export function useSetJobOnBoard() {
    const update = useUpdateJob();
    return {
        ...update,
        /** true pulls the job back onto the active board, false releases it. */
        setOnBoard: (id: string, onBoard: boolean) =>
            update.mutateAsync({ id, patch: { force_active: onBoard } }),
    };
}

// -----------------------------------------------------------------------------
// delete_job - admin only. Permanent.
// -----------------------------------------------------------------------------

/**
 * Deletes a job outright, cascading its stages, flatwork rows, weather
 * observations and holds.
 *
 * Hard delete rather than a hidden flag on purpose. upsert_job's conflict
 * clause does not touch `active`, so a deactivated row hit by a future PO would
 * be updated and stay invisible, and a lot that genuinely started later would
 * never reappear. Deleting lets the sync insert a clean row when real work
 * arrives.
 *
 * The RPC WARNS, it does not block. An earlier version refused any job carrying
 * weather observations, which silently stopped Andrea deleting the Zion 92 and
 * 93 not-released lots: they had weather auto-pulled when Pour completed but no
 * packet had ever gone to a builder. Whether losing that data matters is a
 * judgement only the person holding the dispute can make, so the RPC returns
 * what it removed and the confirmation dialog carries the warning. Two things
 * still raise: a non-admin caller, and a job id that does not exist.
 */
export function useDeleteJob() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (jobId: string) => {
            const { data, error } = await supabase.rpc('delete_job', { p_job_id: jobId });
            if (error) throw error;
            return data as {
                deleted: boolean;
                job: string;
                removed: { weather_rows: number; flatwork_rows: number; stage_rows: number };
            };
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
            qc.invalidateQueries({ queryKey: ['flatworkEvents'] });
            qc.invalidateQueries({ queryKey: ['activityLog'] });
        },
    });
}

// -----------------------------------------------------------------------------
// Clear review flag (admin only)
// -----------------------------------------------------------------------------
export function useClearReviewFlag() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (jobId: string) => {
            const { error } = await supabase
                .from('jobs')
                .update({ needs_review: false, review_reason: null })
                .eq('id', jobId);
            if (error) throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
        },
    });
}

// -----------------------------------------------------------------------------
// submit_service_request - super and admin. Creates a new request.
// -----------------------------------------------------------------------------
export function useSubmitServiceRequest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: SubmitServiceRequestArgs): Promise<ServiceRequest> => {
            const { data, error } = await supabase.rpc('submit_service_request', args);
            if (error) throw error;
            return data as ServiceRequest;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['serviceRequests'] });
            qc.invalidateQueries({ queryKey: ['activityLog'] });
        },
    });
}

// -----------------------------------------------------------------------------
// assign_service_request - admin only. Dispatches a request to a crew.
// -----------------------------------------------------------------------------
export function useAssignServiceRequest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: AssignServiceRequestArgs): Promise<ServiceRequest> => {
            const { data, error } = await supabase.rpc('assign_service_request', args);
            if (error) throw error;
            return data as ServiceRequest;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['serviceRequests'] });
            qc.invalidateQueries({ queryKey: ['activityLog'] });
        },
    });
}

// -----------------------------------------------------------------------------
// advance_service_request_status - field and admin. Moves status forward.
// -----------------------------------------------------------------------------
export function useAdvanceServiceRequestStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: AdvanceServiceRequestStatusArgs): Promise<ServiceRequest> => {
            const { data, error } = await supabase.rpc('advance_service_request_status', args);
            if (error) throw error;
            return data as ServiceRequest;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['serviceRequests'] });
            qc.invalidateQueries({ queryKey: ['activityLog'] });
        },
    });
}

// -----------------------------------------------------------------------------
// cancel_service_request - admin only. Kills a request before completion.
// -----------------------------------------------------------------------------
export function useCancelServiceRequest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: CancelServiceRequestArgs): Promise<ServiceRequest> => {
            const { data, error } = await supabase.rpc('cancel_service_request', args);
            if (error) throw error;
            return data as ServiceRequest;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['serviceRequests'] });
            qc.invalidateQueries({ queryKey: ['activityLog'] });
        },
    });
}
